/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  AdminDollarInput2,
  AdminInput,
  Button,
  Captcha,
  CaptchaSlot,
  Div,
  ErrorToast,
  H3,
  Img,
  Modal,
  SuccessToast,
  deductVDriveBalance,
  increaseVDriveBalance,
  moneyCoins,
  useCaptcha,
  reportError,
} from '@vgrubs/components';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

// TODO make reusable component from this and use also for UserCreditCardEditBalance

export const VDriveCreditCardEditBalance = ({
  id,
  onSaved,
  balance,
  editBalanceModal,
  setEditBalanceModal,
  setLoading,
}: {
  id: string | undefined;
  /** Refetches the balance: fees for being below zero come out first. */
  onSaved: () => void;
  balance: number;
  editBalanceModal: boolean;
  setEditBalanceModal: Dispatch<SetStateAction<boolean>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
}) => {
  const [amount, setAmount] = useState(0);
  const [adminPassword, setAdminPassword] = useState('');
  const captchaRef = useRef<HTMLDivElement>(null);
  const { getCaptchaToken } = useCaptcha(
    process.env.NX_TURNSTILE_SITE_KEY_BALANCE,
    'admin-balance',
    captchaRef,
    // Hide the loader while the user is solving an interactive challenge.
    (isInteractive) => isInteractive && setLoading(false)
  );

  const availableBalance = balance || 0;

  // One token backs whichever of the two endpoints fires — the branches below
  // are mutually exclusive, so the single-use token is never spent twice.
  async function handleEditBalance(captcha: Captcha) {
    try {
      setLoading(true);

      if (Number(amount) < Number(availableBalance)) {
        const amountToDeduct = Number(availableBalance) - Number(amount);

        const body = {
          amount: amountToDeduct,
          adminPassword,
          captcha,
        };

        const { data } = await deductVDriveBalance(id!, body);
        SuccessToast(data.message);
      }

      if (Number(amount) > Number(availableBalance)) {
        const amountToIncrease = Number(amount) - Number(availableBalance);

        const body = {
          amount: amountToIncrease,
          adminPassword,
          captcha,
        };

        const { data } = await increaseVDriveBalance(id!, body);
        SuccessToast(data.message);
      }

      setEditBalanceModal(false);
      setAmount(0);
      setAdminPassword('');
      onSaved();
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async () => {
    if (!amount || !adminPassword)
      return ErrorToast('Please fill in all information');

    if (Number(amount) === availableBalance)
      return ErrorToast('Change Balance to proceed');

    setLoading(true);
    const token = await getCaptchaToken();
    if (!token) {
      setLoading(false);
      return;
    }

    handleEditBalance({ token });
  };

  useEffect(() => {
    if (balance) {
      setAmount(balance);
    }
  }, [balance]);

  const handleAmountChange = useCallback((val) => {
    setAmount(val);
  }, []);

  return (
    <Modal
      isOpen={editBalanceModal}
      closeModal={() => setEditBalanceModal(false)}
      logo={false}
      // header="Add Bank"
    >
      <Img width={230} src={moneyCoins} alt="edit balance" />
      <H3 $m="24px 0">Edit Balance</H3>
      <Div $flex $column $gap="20px" $gapSM="16px">
        <AdminDollarInput2
          label="Available Balance"
          onValueChange={handleAmountChange}
          value={amount}
          $textCenter
        />

        <AdminInput
          label="Admin Password"
          placeholder="Enter here"
          value={adminPassword}
          onChange={(e) => setAdminPassword(e.target.value)}
          $password
          $textCenter
          type="text"
          autoComplete="off"
        />
      </Div>

      <CaptchaSlot ref={captchaRef} />

      <Button $selected $w="100%" $mt="24" $mtSM="16" onClick={handleSubmit}>
        Save
      </Button>
      <Button
        $selected
        $gray
        $w="100%"
        $mt="16"
        onClick={() => setEditBalanceModal(false)}
      >
        Cancel
      </Button>
    </Modal>
  );
};
